"use client";

import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  Input,
  Label,
  Loader,
  RHF,
  toast,
  useDebounceCallback,
  zodResolver,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  BadgePercent,
  Barcode,
  Keyboard,
  Link2,
  ListChecks,
  PackagePlus,
  ScanBarcode,
  Tags,
} from "lucide-react";
import { type ComponentType, useEffect, useMemo, useState } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import { FieldHelpHint } from "@/shared/ui/FieldHelpHint";
import {
  type CatalogSkuMatchRow,
  type CatalogSuggestMatchRow,
  type LinkableCatalogSkuRow,
  shopCatalogApi,
} from "../api/client";
import {
  shopCatalogKeys,
  shopPosKeys,
  useCatalogMatchQuery,
  useCatalogSuggestQuery,
  useMasterCategoriesQuery,
  useProvisionSellableProduct,
} from "../api/queries";
import { getStoredMerchantPosCategoryId } from "../lib/merchant-pos-category-storage";
import { CascadingCategorySelect } from "./CascadingCategorySelect";
import { ShopAddSellablePickCatalogPanel } from "./ShopAddSellablePickCatalogPanel";

type EntryMode = "manual" | "pick";

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK (\u20AD)" },
  { value: "THB", label: "THB (\u0E3F)" },
  { value: "USD", label: "USD ($)" },
];

const TYPE_OPTIONS = [
  {
    value: "part_new",
    label: "\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0ec1\u0e97\u0ec9 (Genuine)",
  },
  { value: "part_oem", label: "\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8 OEM" },
  {
    value: "part_aftermarket",
    label:
      "\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e97\u0ebb\u0e94\u0ec1\u0e97\u0e99 (Aftermarket)",
  },
  {
    value: "part_used",
    label:
      "\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0ea1\u0eb7\u0eaa\u0ead\u0e87 (Used)",
  },
];

/** \u0e84\u0eb3\u0ead\u0eb0\u0e97\u0eb4\u0e9a\u0eb2\u0e8d\u0ec1\u0e95\u0ec8\u0ea5\u0eb0\u0e9b\u0eb0\u0ec0\u0e9e\u0e94\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8 \u2014 \u0eaa\u0eb0\u0ec1\u0e94\u0e87\u0ec3\u0e95\u0ec9 dropdown \u0e95\u0eb2\u0ea1\u0e97\u0eb5\u0ec8\u0ec0\u0ea5\u0eb7\u0ead\u0e81 */
const TYPE_DESCRIPTIONS: Record<string, string> = {
  part_new:
    "\u0ec1\u0ea1\u0ec8\u0e99\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0ec0\u0e81\u0ea3\u0e94\u0ec1\u0e97\u0ec9 \u0e97\u0eb5\u0ec8\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0ec2\u0e94\u0e8d\u0e9a\u0ecd\u0ea5\u0eb4\u0eaa\u0eb1\u0e94\u0ea5\u0ebb\u0e94\u0e8d\u0ebb\u0e99 \u0eab\u0ebc\u0eb7 \u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99\u0e97\u0eb5\u0ec8\u0ec4\u0e94\u0ec9\u0eae\u0eb1\u0e9a\u0ea1\u0ead\u0e9a\u0edd\u0eb2\u0e8d\u0ec2\u0e94\u0e8d\u0e81\u0ebb\u0e87. \u0e8a\u0eb4\u0ec9\u0e99\u0eaa\u0ec8\u0ea7\u0e99\u0e88\u0eb0\u0ea1\u0eb5\u0e81\u0eb2\u0e99\u0e9b\u0eb0\u0e97\u0eb1\u0e9a\u0ec2\u0ea5\u0ec2\u0e81\u0ec9\u0e82\u0ead\u0e87\u0e8d\u0eb5\u0ec8\u0eab\u0ecd\u0ec9\u0ea5\u0ebb\u0e94\u0e8d\u0ebb\u0e99\u0e99\u0eb1\u0ec9\u0e99\u0ec6. \u0ea1\u0eb5\u0e84\u0ea7\u0eb2\u0ea1\u0edd\u0eb1\u0ec9\u0e99\u0ec3\u0e88\u0ec3\u0e99\u0ea1\u0eb2\u0e94\u0e95\u0eb0\u0e96\u0eb2\u0e99, \u0e84\u0ea7\u0eb2\u0ea1\u0e9b\u0ead\u0e94\u0ec4\u0e9e \u0ec1\u0ea5\u0eb0 \u0e82\u0eb0\u0edc\u0eb2\u0e94\u0e97\u0eb5\u0ec8\u0ec0\u0edd\u0eb2\u0eb0\u0eaa\u0ebb\u0ea1\u0e81\u0eb1\u0e9a\u0ea5\u0ebb\u0e94\u0ec2\u0e94\u0e8d\u0e81\u0ebb\u0e87. \u0ec1\u0e95\u0ec8\u0e88\u0eb0\u0ea1\u0eb5\u0ea5\u0eb2\u0e84\u0eb2\u0eaa\u0eb9\u0e87\u0e97\u0eb5\u0ec8\u0eaa\u0eb8\u0e94.",
  part_oem:
    "\u0ec1\u0ea1\u0ec8\u0e99\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e97\u0eb5\u0ec8\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0ec2\u0e94\u0e8d\u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99\u0e9c\u0eb9\u0ec9\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0e8a\u0eb4\u0ec9\u0e99\u0eaa\u0ec8\u0ea7\u0e99\u0e97\u0eb5\u0ec8\u0ea1\u0eb5\u0ea1\u0eb2\u0e94\u0e95\u0eb0\u0e96\u0eb2\u0e99 \u0ec1\u0ea5\u0eb0 \u0ead\u0eb2\u0e94\u0ec0\u0e9b\u0eb1\u0e99\u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99\u0e94\u0ebd\u0ea7\u0e81\u0eb1\u0e9a\u0e97\u0eb5\u0ec8\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0ec3\u0eab\u0ec9\u0ea5\u0ebb\u0e94\u0e8d\u0ebb\u0e99. \u0ec1\u0e95\u0ec8\u0e88\u0eb0\u0ec3\u0e8a\u0ec9\u0e8d\u0eb5\u0ec8\u0eab\u0ecd\u0ec9\u0e82\u0ead\u0e87\u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99\u0e9c\u0eb9\u0ec9\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0ec0\u0ead\u0e87 \u0ec1\u0e97\u0e99\u0e81\u0eb2\u0e99\u0ec3\u0e8a\u0ec9\u0ec2\u0ea5\u0ec2\u0e81\u0ec9\u0e82\u0ead\u0e87\u0e8d\u0eb5\u0ec8\u0eab\u0ecd\u0ec9\u0ea5\u0ebb\u0e94. \u0e84\u0eb8\u0e99\u0e99\u0eb0\u0e9e\u0eb2\u0e9a\u0ea7\u0eb1\u0e94\u0eaa\u0eb0\u0e94\u0eb8 \u0ec1\u0ea5\u0eb0 \u0e81\u0eb2\u0e99\u0e99\u0eb3\u0ec3\u0e8a\u0ec9\u0ead\u0eb2\u0e94\u0ec3\u0e81\u0ec9\u0e84\u0ebd\u0e87\u0e81\u0eb1\u0e9a\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0ec1\u0e97\u0ec9 \u0ec3\u0e99\u0ea5\u0eb2\u0e84\u0eb2\u0e97\u0eb5\u0ec8\u0e9b\u0eb0\u0ea2\u0eb1\u0e94\u0e81\u0ea7\u0ec8\u0eb2.",
  part_aftermarket:
    "\u0ec1\u0ea1\u0ec8\u0e99\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e97\u0ebb\u0e94\u0ec1\u0e97\u0e99\u0e97\u0eb5\u0ec8\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0ec2\u0e94\u0e8d\u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99\u0e97\u0ebb\u0ec8\u0ea7\u0ec4\u0e9b \u0ec0\u0e8a\u0eb4\u0ec8\u0e87\u0e9a\u0ecd\u0ec8\u0ec1\u0ea1\u0ec8\u0e99\u0e9c\u0eb9\u0ec9\u0e9c\u0eb0\u0ea5\u0eb4\u0e94\u0e8a\u0eb4\u0ec9\u0e99\u0eaa\u0ec8\u0ea7\u0e99\u0ec2\u0e94\u0e8d\u0e81\u0ebb\u0e87\u0ec3\u0eab\u0ec9\u0e81\u0eb1\u0e9a\u0e8d\u0eb5\u0ec8\u0eab\u0ecd\u0ec9\u0ea5\u0ebb\u0e94\u0e8d\u0ebb\u0e99. \u0e84\u0eb8\u0e99\u0e99\u0eb0\u0e9e\u0eb2\u0e9a, \u0e84\u0ea7\u0eb2\u0ea1\u0e97\u0ebb\u0e99\u0e97\u0eb2\u0e99 \u0ec1\u0ea5\u0eb0 \u0ea5\u0eb2\u0e84\u0eb2 \u0ea1\u0eb5\u0eab\u0ebc\u0eb2\u0e8d\u0ea5\u0eb0\u0e94\u0eb1\u0e9a \u0e82\u0eb6\u0ec9\u0e99\u0ea2\u0eb9\u0ec8\u0e81\u0eb1\u0e9a\u0e8d\u0eb5\u0ec8\u0eab\u0ecd\u0ec9 \u0ec1\u0ea5\u0eb0 \u0e9c\u0eb9\u0ec9\u0e9c\u0eb0\u0ea5\u0eb4\u0e94. \u0ec0\u0edd\u0eb2\u0eb0\u0eaa\u0eb3\u0ea5\u0eb1\u0e9a\u0ea5\u0eb9\u0e81\u0e84\u0ec9\u0eb2\u0e97\u0eb5\u0ec8\u0ec0\u0e99\u0eb1\u0ec9\u0e99\u0e84\u0ea7\u0eb2\u0ea1\u0e9b\u0eb0\u0ea2\u0eb1\u0e94 \u0ec1\u0ea5\u0eb0 \u0ea1\u0eb1\u0e81\u0ec3\u0e8a\u0ec9\u0e81\u0eb1\u0e9a\u0e8a\u0eb4\u0ec9\u0e99\u0eaa\u0ec8\u0ea7\u0e99\u0e97\u0eb5\u0ec8\u0e9a\u0ecd\u0ec8\u0e81\u0eb0\u0e97\u0ebb\u0e9a\u0e95\u0ecd\u0ec8\u0ea5\u0eb0\u0e9a\u0ebb\u0e9a\u0e84\u0ea7\u0eb2\u0ea1\u0e9b\u0ead\u0e94\u0ec4\u0e9e\u0eab\u0ebc\u0eb1\u0e81\u0e82\u0ead\u0e87\u0ea5\u0ebb\u0e94.",
  part_used:
    "\u0ec1\u0ea1\u0ec8\u0e99\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e97\u0eb5\u0ec8\u0e9c\u0ec8\u0eb2\u0e99\u0e81\u0eb2\u0e99\u0e99\u0eb3\u0ec3\u0e8a\u0ec9\u0ea1\u0eb2\u0ec1\u0ea5\u0ec9\u0ea7 \u0ec2\u0e94\u0e8d\u0eaa\u0ec8\u0ea7\u0e99\u0ec3\u0eab\u0e8d\u0ec8\u0ec1\u0ea1\u0ec8\u0e99\u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e88\u0eb2\u0e81\u0ea5\u0ebb\u0e94\u0ea1\u0eb7\u0eaa\u0ead\u0e87 \u0eab\u0ebc\u0eb7 \u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e97\u0eb5\u0ec8\u0e96\u0ead\u0e94\u0ead\u0ead\u0e81\u0e88\u0eb2\u0e81\u0ea5\u0ebb\u0e94\u0e84\u0eb1\u0e99\u0ead\u0eb7\u0ec8\u0e99 \u0ec0\u0e8a\u0eb1\u0ec8\u0e99 \u0ead\u0eb0\u0ec4\u0eab\u0ebc\u0ec8\u0e88\u0eb2\u0e81\u0eae\u0ec9\u0eb2\u0e99\u0e8a\u0ebd\u0e87\u0e81\u0ebb\u0e87. \u0ec0\u0e9b\u0eb1\u0e99\u0e97\u0eb2\u0e87\u0ec0\u0ea5\u0eb7\u0ead\u0e81\u0e97\u0eb5\u0ec8\u0ea1\u0eb5\u0ea5\u0eb2\u0e84\u0eb2\u0e96\u0eb7\u0e81\u0e97\u0eb5\u0ec8\u0eaa\u0eb8\u0e94.",
};

const FormSchema = z.object({
  name: z.string().max(255).optional(),
  description: z.string().max(5000).optional(),
  type: z.enum(["part_new", "part_oem", "part_aftermarket", "part_used"]),
  categoryId: z.string().optional(),
  shopSku: z
    .string()
    .min(1, "\u0e95\u0ec9\u0ead\u0e87\u0ec3\u0eaa\u0ec8 Shop SKU")
    .max(100),
  barcode: z.string().max(100).optional(),
  price: z.coerce
    .number()
    .min(
      0,
      "\u0ea5\u0eb2\u0e84\u0eb2\u0e95\u0ec9\u0ead\u0e87\u0e9a\u0ecd\u0ec8\u0e95\u0ec8\u0eb3\u0e81\u0ea7\u0ec8\u0eb2 0",
    ),
  currency: z.enum(["LAK", "THB", "USD"]),
  globalSku: z.string().max(100).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

type ShopAddSellableProductModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  prefillBarcode?: string;
  prefillBranchId?: string;
  defaultEntryMode?: EntryMode;
  onSuccess?: () => void;
};

function matchKindLabel(kind: CatalogSkuMatchRow["matchKind"]): string {
  if (kind === "alternate") return "Alternate";
  if (kind === "supersession") return "Supersession";
  return "OEM";
}

function currencySymbol(currency: FormValues["currency"]) {
  if (currency === "THB") return "\u0E3F";
  if (currency === "USD") return "$";
  return "\u20AD";
}

function SectionHeader({
  icon: Icon,
  title,
  hint,
}: {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-3 flex items-start gap-2.5">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-4" aria-hidden />
      </div>
      <div className="min-w-0 pt-0.5">
        <h3 className="font-semibold text-foreground text-sm leading-none">
          {title}
        </h3>
        {hint ? (
          <p className="mt-1 text-muted-foreground text-xs leading-snug">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function ShopAddSellableProductModal({
  open,
  onOpenChange,
  shopId,
  prefillBarcode = "",
  prefillBranchId,
  defaultEntryMode = "manual",
  onSuccess,
}: ShopAddSellableProductModalProps) {
  const qc = useQueryClient();
  const provision = useProvisionSellableProduct(shopId);
  const masterCategoriesQuery = useMasterCategoriesQuery(
    open ? shopId : undefined,
  );
  const [entryMode, setEntryMode] = useState<EntryMode>("manual");
  const [pickCategoryId, setPickCategoryId] = useState("");
  const [pickProductId, setPickProductId] = useState<string | null>(null);
  const [checkedSkus, setCheckedSkus] = useState<LinkableCatalogSkuRow[]>([]);
  const [pickPrice, setPickPrice] = useState(0);
  const [pickCurrency, setPickCurrency] =
    useState<FormValues["currency"]>("LAK");
  const [pickSubmitting, setPickSubmitting] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<CatalogSkuMatchRow | null>(
    null,
  );
  const [forceCreateNew, setForceCreateNew] = useState(false);
  const [debouncedOem, setDebouncedOem] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const debouncedSyncOem = useDebounceCallback((val: string) => {
    setDebouncedOem(val.trim());
  }, 350);
  const debouncedSyncName = useDebounceCallback((val: string) => {
    setDebouncedName(val.trim());
  }, 400);

  const form = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema) as Resolver<FormValues>,
    defaultValues: {
      name: "",
      description: "",
      type: "part_new",
      categoryId: "",
      shopSku: "",
      barcode: "",
      price: 0,
      currency: "LAK",
      globalSku: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    const bc = prefillBarcode?.trim() ?? "";
    const storedCategoryId =
      prefillBranchId != null
        ? (getStoredMerchantPosCategoryId(shopId, prefillBranchId) ?? "")
        : "";
    form.reset({
      name: "",
      description: "",
      type: "part_new",
      categoryId: storedCategoryId,
      shopSku: "",
      barcode: bc || "",
      price: 0,
      currency: "LAK",
      globalSku: "",
    });
    setSelectedMatch(null);
    setForceCreateNew(false);
    setDebouncedOem("");
    setDebouncedName("");
    setEntryMode(defaultEntryMode);
    setPickCategoryId(storedCategoryId);
    setPickProductId(null);
    setCheckedSkus([]);
    setPickPrice(0);
    setPickCurrency("LAK");
  }, [open, prefillBarcode, prefillBranchId, shopId, defaultEntryMode, form]);

  const globalSku = form.watch("globalSku");
  const productName = form.watch("name");
  const productType = form.watch("type");
  const categoryId = form.watch("categoryId");

  useEffect(() => {
    debouncedSyncOem(globalSku ?? "");
  }, [globalSku, debouncedSyncOem]);

  useEffect(() => {
    debouncedSyncName(productName ?? "");
  }, [productName, debouncedSyncName]);

  const matchQuery = useCatalogMatchQuery(
    open ? shopId : undefined,
    { oemPartNo: debouncedOem },
    open && !forceCreateNew && !selectedMatch && debouncedOem.length >= 2,
  );

  const suggestQuery = useCatalogSuggestQuery(
    open ? shopId : undefined,
    {
      name: debouncedName,
      categoryId: categoryId || undefined,
      type: productType,
    },
    open &&
      !forceCreateNew &&
      !selectedMatch &&
      debouncedName.length >= 3 &&
      !debouncedOem,
  );

  const linkMode = Boolean(selectedMatch);
  const showMatchPanel =
    !linkMode &&
    !forceCreateNew &&
    debouncedOem.length >= 2 &&
    (matchQuery.isLoading || (matchQuery.data?.items.length ?? 0) > 0);

  const showSuggestPanel =
    !linkMode &&
    !forceCreateNew &&
    debouncedName.length >= 3 &&
    !debouncedOem &&
    (suggestQuery.isLoading || (suggestQuery.data?.items.length ?? 0) > 0);

  const pickSuggest = (item: CatalogSuggestMatchRow) => {
    setSelectedMatch({
      catalogProductId: item.catalogProductId,
      catalogSkuId: item.catalogSkuId,
      productName: item.productName,
      oemPartNo: item.oemPartNo,
      matchKind: "oem_exact",
      matchedVia: `suggest ${Math.round(item.score * 100)}%`,
    });
    setForceCreateNew(false);
  };

  const onSubmit = async (vals: FormValues) => {
    if (!selectedMatch) {
      if (!vals.name?.trim()) {
        form.setError("name", {
          message:
            "\u0e95\u0ec9\u0ead\u0e87\u0ec3\u0eaa\u0ec8\u0e8a\u0eb7\u0ec8\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2",
        });
        return;
      }
      if (!vals.categoryId) {
        form.setError("categoryId", {
          message:
            "\u0e95\u0ec9\u0ead\u0e87\u0ec0\u0ea5\u0eb7\u0ead\u0e81\u0edc\u0ea7\u0e94",
        });
        return;
      }
    }

    try {
      if (selectedMatch) {
        await provision.mutateAsync({
          catalogSkuId: selectedMatch.catalogSkuId,
          shopSku: vals.shopSku.trim(),
          barcode: vals.barcode?.trim() || undefined,
          price: vals.price,
          currency: vals.currency,
          type: "part_new",
        });
      } else {
        await provision.mutateAsync({
          name: vals.name.trim(),
          description: vals.description?.trim() || undefined,
          type: vals.type,
          categoryId: vals.categoryId,
          shopSku: vals.shopSku.trim(),
          barcode: vals.barcode?.trim() || undefined,
          price: vals.price,
          currency: vals.currency,
          globalSku: vals.globalSku?.trim() || undefined,
        });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // toast from mutation
    }
  };

  const onPickSubmit = async () => {
    if (checkedSkus.length === 0) {
      toast.error("ກະລຸນາເລືອກ OEM ຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }
    if (pickPrice < 0) {
      toast.error("ລາຄາຕ້ອງບໍ່ຕິດລົບ");
      return;
    }

    setPickSubmitting(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const sku of checkedSkus) {
        const shopSku =
          sku.oemPartNo?.trim() ||
          `SKU-${sku.catalogSkuId.replace(/-/g, "").slice(0, 10)}`;
        try {
          await shopCatalogApi.provisionSellableProduct(shopId, {
            catalogSkuId: sku.catalogSkuId,
            shopSku,
            price: pickPrice,
            currency: pickCurrency,
            type: "part_new",
          });
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      if (shopId) {
        void qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        void qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
      if (ok > 0) {
        toast.success(
          fail > 0
            ? `ຜູກສຳເລັດ ${ok} ລາຍການ · ລົ້ມເຫຼວ ${fail} ລາຍການ`
            : `ຜູກກັບ catalog ${ok} ລາຍການ — ຂາຍໄດ້ທັນທີ`,
        );
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast.error("ຜູກບໍ່ສຳເລັດ — ອາດມີໃນຮ້ານແລ້ວ ຫຼື Shop SKU ຊ້ຳ");
      }
    } finally {
      setPickSubmitting(false);
    }
  };

  const currency = form.watch("currency");
  const barcode = form.watch("barcode");
  const priceSymbol = useMemo(() => currencySymbol(currency), [currency]);
  const scannedBarcode = Boolean(prefillBarcode?.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(94vh,860px)] w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl lg:max-w-5xl">
        <DialogHeader className="shrink-0 space-y-1 border-border border-b bg-muted/25 px-4 py-3.5 sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <span className="flex size-9 items-center justify-center rounded-xl bg-primary/12 text-primary">
              <PackagePlus className="size-5" aria-hidden />
            </span>
            {
              "\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2\u0ec3\u0edd\u0ec8"
            }
          </DialogTitle>
          <DialogDescription className="text-left text-muted-foreground text-xs sm:text-sm">
            {
              "\u0eaa\u0ec9\u0eb2\u0e87\u0ec3\u0e99 catalog \u0ec1\u0ea5\u0eb0\u0ec0\u0e9b\u0eb5\u0e94\u0e82\u0eb2\u0e8d\u0ec3\u0e99\u0eae\u0ec9\u0eb2\u0e99\u0eb5\u0ec9\u0ec4\u0e94\u0ec9\u0e97\u0eb1\u0e99\u0e97\u0eb5 \u2014 \u0e9a\u0ecd\u0ec8\u0e95\u0ec9\u0ead\u0e87\u0ea5\u0ecd Admin"
            }
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex shrink-0 gap-1 border-border border-b bg-muted/15 px-4 py-2.5 sm:px-6"
          role="tablist"
          aria-label={"ວິທີເພີ່ມສິນຄ້າ"}
        >
          <Button
            type="button"
            role="tab"
            aria-selected={entryMode === "manual"}
            variant={entryMode === "manual" ? "default" : "ghost"}
            size="sm"
            className="gap-1.5"
            onClick={() => setEntryMode("manual")}
          >
            <Keyboard className="size-4" aria-hidden />
            {"ປ້ອນເອງ"}
          </Button>
          <Button
            type="button"
            role="tab"
            aria-selected={entryMode === "pick"}
            variant={entryMode === "pick" ? "default" : "ghost"}
            size="sm"
            className="gap-1.5"
            onClick={() => setEntryMode("pick")}
          >
            <ListChecks className="size-4" aria-hidden />
            {"ເລືອກຈາກລະບົບ"}
          </Button>
        </div>

        {entryMode === "pick" ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
              {scannedBarcode ? (
                <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-primary/25 bg-primary/6 px-3 py-2.5">
                  <ScanBarcode
                    className="mt-0.5 size-4 shrink-0 text-primary"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-xs">
                      {
                        "\u0e9e\u0ebb\u0e9a barcode \u0e88\u0eb2\u0e81\u0e81\u0eb2\u0e99\u0eaa\u0eb0\u0ec1\u0e81\u0e99"
                      }
                    </p>
                    <p className="mt-0.5 break-all font-mono text-muted-foreground text-xs">
                      {prefillBarcode.trim()}
                    </p>
                  </div>
                </div>
              ) : null}

              <ShopAddSellablePickCatalogPanel
                shopId={shopId}
                categories={masterCategoriesQuery.data?.items ?? []}
                categoriesLoading={masterCategoriesQuery.isLoading}
                categoryId={pickCategoryId}
                onCategoryChange={setPickCategoryId}
                selectedProductId={pickProductId}
                onSelectProduct={setPickProductId}
                checkedSkus={checkedSkus}
                onCheckedSkusChange={setCheckedSkus}
              />

              <section className="mt-5 rounded-xl border border-border bg-muted/20 p-3">
                <SectionHeader
                  icon={BadgePercent}
                  title={"\u0ea5\u0eb2\u0e84\u0eb2\u0e82\u0eb2\u0e8d"}
                  hint={"ໃຊ້ລາຄານີ້ກັບທຸກ OEM ທີ່ເລືອກ"}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="pick-price">
                      {"\u0ea5\u0eb2\u0e84\u0eb2"}{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="pick-price"
                      type="number"
                      min={0}
                      step="any"
                      value={pickPrice}
                      onChange={(e) =>
                        setPickPrice(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pick-currency">
                      {"\u0eaa\u0eb0\u0e81\u0eb8\u0e99\u0ec0\u0e87\u0eb4\u0e99"}
                    </Label>
                    <select
                      id="pick-currency"
                      value={pickCurrency}
                      onChange={(e) =>
                        setPickCurrency(
                          e.target.value as FormValues["currency"],
                        )
                      }
                      className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {CURRENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>
            </div>

            <DialogFooter className="shrink-0 gap-2 border-border border-t bg-background px-4 py-3 sm:flex-row sm:justify-between sm:px-6">
              <p className="hidden text-muted-foreground text-xs sm:block sm:max-w-[52%] sm:text-left">
                {checkedSkus.length > 0
                  ? `ເລືອກ ${checkedSkus.length} OEM — Shop SKU ຕາມ OEM ອັດຕະໂນມັດ`
                  : "ເລືອກໝວດ → ສິນຄ້າ → check OEM ທີ່ຕ້ອງການຂາຍ"}
              </p>
              <div className="flex w-full gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 sm:flex-none"
                  onClick={() => onOpenChange(false)}
                >
                  {"\u0e8d\u0ebb\u0e81\u0ec0\u0ea5\u0eb5\u0e81"}
                </Button>
                <Button
                  type="button"
                  className="min-w-36 flex-1 gap-2 sm:flex-none"
                  disabled={
                    pickSubmitting ||
                    checkedSkus.length === 0 ||
                    !pickCategoryId
                  }
                  onClick={() => void onPickSubmit()}
                >
                  {pickSubmitting ? <Loader className="size-4" /> : null}
                  {checkedSkus.length > 0
                    ? `ຜູກກັບ ${checkedSkus.length} ລາຍການ`
                    : "ຜູກກັບ catalog"}
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <FormRoot<FormValues>
            methods={form}
            onSubmit={(v) => void onSubmit(v)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
              {scannedBarcode ? (
                <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-primary/25 bg-primary/6 px-3 py-2.5">
                  <ScanBarcode
                    className="mt-0.5 size-4 shrink-0 text-primary"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-xs">
                      {
                        "\u0e9e\u0ebb\u0e9a barcode \u0e88\u0eb2\u0e81\u0e81\u0eb2\u0e99\u0eaa\u0eb0\u0ec1\u0e81\u0e99"
                      }
                    </p>
                    <p className="mt-0.5 break-all font-mono text-muted-foreground text-xs">
                      {prefillBarcode.trim()}
                    </p>
                  </div>
                </div>
              ) : null}

              {linkMode && selectedMatch ? (
                <section className="mb-4 rounded-xl border border-primary/25 bg-primary/6 p-4 md:hidden">
                  <SectionHeader
                    icon={Link2}
                    title={"ຜູກກັບ catalog"}
                    hint={"ເປີດຂາຍໄດ້ທັນທີ — ໃຊ້ຂໍ້ມູນສິນຄ້າຫຼັກທີ່ມີແລ້ວ"}
                  />
                  <p className="font-medium text-foreground text-sm">
                    {selectedMatch.productName}
                  </p>
                  {selectedMatch.oemPartNo ? (
                    <p className="mt-1 font-mono text-muted-foreground text-xs">
                      OEM: {selectedMatch.oemPartNo}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-3 h-8 px-2 text-xs"
                    onClick={() => {
                      setSelectedMatch(null);
                      setForceCreateNew(false);
                    }}
                  >
                    {"ປ່ຽນແບບ"}
                  </Button>
                </section>
              ) : null}

              <div className="grid gap-5 md:grid-cols-2 md:gap-6 lg:gap-8">
                {/* Left column — ซ่อนเมื่อ link กับ master เดิม */}
                <div
                  className={cn(
                    "space-y-5 md:border-border md:border-r md:pr-6 lg:pr-8",
                    linkMode && "hidden md:block",
                  )}
                >
                  {linkMode && selectedMatch ? (
                    <section className="rounded-xl border border-primary/25 bg-primary/6 p-4">
                      <SectionHeader
                        icon={Link2}
                        title={"ຜູກກັບ catalog"}
                        hint={"ເປີດຂາຍໄດ້ທັນທີ — ໃຊ້ຂໍ້ມູນສິນຄ້າຫຼັກທີ່ມີແລ້ວ"}
                      />
                      <p className="font-medium text-foreground text-sm">
                        {selectedMatch.productName}
                      </p>
                      {selectedMatch.oemPartNo ? (
                        <p className="mt-1 font-mono text-muted-foreground text-xs">
                          OEM: {selectedMatch.oemPartNo}
                        </p>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-3 h-8 px-2 text-xs"
                        onClick={() => {
                          setSelectedMatch(null);
                          setForceCreateNew(false);
                        }}
                      >
                        {"ປ່ຽນແບບ"}
                      </Button>
                    </section>
                  ) : null}

                  {!linkMode ? (
                    <>
                      <section>
                        <SectionHeader
                          icon={PackagePlus}
                          title={
                            "\u0e82\u0ecd\u0ec9\u0ea1\u0eb9\u0e99\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"
                          }
                          hint={
                            "\u0e8a\u0eb7\u0ec8\u0e97\u0eb5\u0ec8\u0e88\u0eb0\u0ec0\u0eab\u0eb1\u0e99\u0ec3\u0e99 POS \u0ec1\u0ea5\u0eb0\u0ec3\u0e9a\u0e9a\u0eb4\u0e99"
                          }
                        />
                        <div className="space-y-3">
                          <FormInput
                            name="name"
                            label={
                              "\u0e8a\u0eb7\u0ec8\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"
                            }
                            requiredMark
                            placeholder={
                              "\u0e95\u0ebb\u0ea7\u0e8d\u0ec8\u0eb2\u0e87: \u0e9c\u0ec9\u0eb2\u0ec0\u0e9a\u0e93\u0e81\u0edc\u0ec9\u0eb2 Toyota ..."
                            }
                          />
                          {showSuggestPanel ? (
                            <div className="space-y-2 rounded-xl border border-primary/30 border-dashed bg-primary/5 p-3">
                              <p className="font-medium text-foreground text-xs">
                                {"ນ່າຈະເປັນຕົວນີ້? (ບໍ່ auto-link)"}
                              </p>
                              <ul className="space-y-2">
                                {suggestQuery.data?.items.map((item) => (
                                  <li
                                    key={item.catalogSkuId}
                                    className="flex items-start justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2"
                                  >
                                    <div className="min-w-0">
                                      <p className="truncate font-medium text-foreground text-sm">
                                        {item.productName}
                                      </p>
                                      <p className="mt-0.5 text-muted-foreground text-xs">
                                        {Math.round(item.score * 100)}% ·{" "}
                                        {item.reasons.join(", ")}
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      className="shrink-0 gap-1"
                                      onClick={() => pickSuggest(item)}
                                    >
                                      <Link2 className="size-3.5" aria-hidden />
                                      {"ໃຊ້ລາຍການນີ້"}
                                    </Button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          <FormSelect
                            name="type"
                            label={
                              "\u0e9b\u0eb0\u0ec0\u0e9e\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"
                            }
                            options={TYPE_OPTIONS}
                          />
                          {TYPE_DESCRIPTIONS[productType] ? (
                            <p className="mt-1.5 rounded-md bg-muted/40 p-2 text-[11px] text-muted-foreground leading-relaxed">
                              {TYPE_DESCRIPTIONS[productType]}
                            </p>
                          ) : null}
                        </div>
                      </section>

                      <div className="border-border border-t" />

                      <section>
                        <SectionHeader
                          icon={Tags}
                          title={
                            "\u0edc\u0ea7\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"
                          }
                          hint={
                            "\u0ec0\u0ea5\u0eb7\u0ead\u0e81\u0e88\u0eb2\u0e81\u0edc\u0ea7\u0e94\u0eab\u0ebc\u0eb1\u0e81\u0ec4\u0e9b\u0eab\u0eb2\u0edc\u0ea7\u0e94\u0e8d\u0ec8\u0ead\u0e8d\u0e88\u0ebb\u0e99\u0eaa\u0eb8\u0e94"
                          }
                        />
                        <div className="space-y-1.5">
                          <Label>
                            {"\u0edc\u0ea7\u0e94"}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <CascadingCategorySelect
                            categories={masterCategoriesQuery.data?.items ?? []}
                            value={categoryId ?? ""}
                            onChange={(val) =>
                              form.setValue("categoryId", val, {
                                shouldValidate: true,
                              })
                            }
                            isLoading={masterCategoriesQuery.isLoading}
                            clearable
                            requireLeaf
                          />
                          {form.formState.errors.categoryId ? (
                            <p className="text-destructive text-xs">
                              {form.formState.errors.categoryId.message}
                            </p>
                          ) : null}
                        </div>
                      </section>
                    </>
                  ) : null}
                </div>

                {/* Right column */}
                <div className="space-y-5">
                  <section>
                    <SectionHeader
                      icon={Barcode}
                      title={
                        "\u0ea5\u0eb0\u0eab\u0eb1\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"
                      }
                      hint={
                        "Shop SKU \u0ec3\u0e8a\u0ec9\u0eaa\u0eb0\u0ec1\u0e81\u0e99/\u0e84\u0ebb\u0ec9\u0e99\u0eab\u0eb2\u0ec3\u0e99\u0eae\u0ec9\u0eb2\u0e99"
                      }
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormInput
                        name="shopSku"
                        label={
                          <FieldHelpHint label="Shop SKU">
                            {
                              "\u0ea5\u0eb0\u0eab\u0eb1\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2\u0e82\u0ead\u0e87\u0eae\u0ec9\u0eb2\u0e99 \u2014 \u0ec3\u0e8a\u0ec9\u0ec3\u0e99 POS / \u0eaa\u0eb0\u0ec1\u0e81\u0e99 / \u0e84\u0ebb\u0ec9\u0e99\u0eab\u0eb2. \u0ec1\u0e95\u0e81\u0e95\u0ec8\u0eb2\u0e87\u0e88\u0eb2\u0e81 OEM / \u0ea5\u0eb0\u0eab\u0eb1\u0e94\u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99."
                            }
                          </FieldHelpHint>
                        }
                        requiredMark
                        placeholder={
                          "\u0ea5\u0eb0\u0eab\u0eb1\u0e94\u0eae\u0ec9\u0eb2\u0e99"
                        }
                      />
                      <FormInput
                        name="barcode"
                        label="Barcode"
                        placeholder={
                          "\u0eaa\u0eb0\u0ec1\u0e81\u0e99 \u0eab\u0ebc\u0eb7 \u0e9b\u0ec9\u0ead\u0e99\u0ea1\u0eb7"
                        }
                      />
                      <div className="sm:col-span-2">
                        <FormInput
                          name="globalSku"
                          label={
                            "OEM / \u0ea5\u0eb0\u0eab\u0eb1\u0e94\u0ec2\u0eae\u0e87\u0e87\u0eb2\u0e99 (\u0e97\u0eb2\u0e87\u0ec0\u0ea5\u0eb7\u0ead\u0e81)"
                          }
                          placeholder="\u2014"
                        />
                      </div>
                    </div>

                    {showMatchPanel ? (
                      <div className="mt-3 space-y-2 rounded-xl border border-border bg-muted/15 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-foreground text-xs">
                            {"ພົບສິນຄ້າ catalog ທີ່ຕົງກັນ"}
                          </p>
                          {matchQuery.isLoading ? (
                            <Loader className="size-3.5 text-muted-foreground" />
                          ) : null}
                        </div>
                        <ul className="space-y-2">
                          {matchQuery.data?.items.map((item) => (
                            <li
                              key={item.catalogSkuId}
                              className="flex items-start justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2"
                            >
                              <div className="min-w-0">
                                <p className="truncate font-medium text-foreground text-sm">
                                  {item.productName}
                                </p>
                                {item.oemPartNo ? (
                                  <p className="mt-0.5 font-mono text-muted-foreground text-xs">
                                    OEM: {item.oemPartNo}
                                  </p>
                                ) : null}
                                <p className="mt-0.5 text-muted-foreground text-xs">
                                  {matchKindLabel(item.matchKind)}
                                  {item.matchedVia
                                    ? `: ${item.matchedVia}`
                                    : ""}
                                </p>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="shrink-0 gap-1"
                                onClick={() => {
                                  setSelectedMatch(item);
                                  setForceCreateNew(false);
                                }}
                              >
                                <Link2 className="size-3.5" aria-hidden />
                                {"ໃຊ້ລາຍການນີ້"}
                              </Button>
                            </li>
                          ))}
                        </ul>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => {
                            setForceCreateNew(true);
                            setSelectedMatch(null);
                          }}
                        >
                          {"ບໍ່ພົບ — ສ້າງລາຍການໃໝ່ໃນ catalog"}
                        </Button>
                      </div>
                    ) : null}

                    {forceCreateNew && debouncedOem.length >= 2 ? (
                      <p className="mt-2 text-muted-foreground text-xs">
                        {"ໂໝດສ້າງລາຍການໃໝ່ໃນ catalog"}
                      </p>
                    ) : null}
                    {barcode?.trim() ? (
                      <p className="mt-2 flex items-center gap-1.5 text-muted-foreground text-xs">
                        <Barcode className="size-3.5" aria-hidden />
                        {
                          "\u0e88\u0eb0\u0eaa\u0eb0\u0ec1\u0e81\u0e99\u0e82\u0eb2\u0e8d\u0e94\u0ec9\u0ea7\u0e8d "
                        }
                        <span className="font-mono text-foreground">
                          {barcode.trim()}
                        </span>
                      </p>
                    ) : null}
                  </section>

                  <div className="border-border border-t" />

                  <section>
                    <SectionHeader
                      icon={BadgePercent}
                      title={"\u0ea5\u0eb2\u0e84\u0eb2\u0e82\u0eb2\u0e8d"}
                      hint={
                        "\u0ea5\u0eb2\u0e84\u0eb2\u0ec0\u0ea5\u0eb5\u0ec8\u0ea1\u0e95\u0ebb\u0ec9\u0e99\u0ec0\u0ea1\u0eb7\u0ec8\u0ead\u0ec0\u0e9b\u0eb5\u0e94\u0e82\u0eb2\u0e8d"
                      }
                    />
                    <div className="grid gap-3 rounded-xl border border-border bg-muted/20 p-3 sm:grid-cols-2">
                      <FormPrice
                        name="price"
                        label={"\u0ea5\u0eb2\u0e84\u0eb2"}
                        requiredMark
                        currencySymbol={priceSymbol}
                      />
                      <FormSelect
                        name="currency"
                        label={
                          "\u0eaa\u0eb0\u0e81\u0eb8\u0e99\u0ec0\u0e87\u0eb4\u0e99"
                        }
                        options={CURRENCY_OPTIONS}
                      />
                    </div>
                  </section>

                  {!linkMode ? (
                    <section className="space-y-2">
                      <Label htmlFor="add-sellable-desc">
                        {
                          "\u0ea5\u0eb2\u0e8d\u0ea5\u0eb0\u0ead\u0ebd\u0e94 (\u0e97\u0eb2\u0e87\u0ec0\u0ea5\u0eb7\u0ead\u0e81)"
                        }
                      </Label>
                      <RHF.Controller
                        name="description"
                        control={form.control}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            id="add-sellable-desc"
                            rows={4}
                            placeholder={
                              "\u0edd\u0eb2\u0e8d\u0ec0\u0eab\u0e94\u0eaa\u0eb3\u0ea5\u0eb1\u0e9a\u0e9e\u0eb0\u0e99\u0eb1\u0e81\u0e87\u0eb2\u0e99 / \u0ea5\u0eb2\u0e8d\u0ea5\u0eb0\u0ead\u0ebd\u0e94\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1..."
                            }
                            className="flex min-h-24 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        )}
                      />
                    </section>
                  ) : null}
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 gap-2 border-border border-t bg-background px-4 py-3 sm:flex-row sm:justify-between sm:px-6">
              <p className="hidden text-muted-foreground text-xs sm:block sm:max-w-[52%] sm:text-left">
                {linkMode
                  ? "ກົດບັນທຶກເພື່ອຜູກກັບ catalog ແລະເປີດຂາຍໃນຮ້ານ"
                  : "\u0eab\u0ebc\u0eb1\u0e87\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81\u0eaa\u0eb2\u0ea1\u0eb2\u0e94\u0eaa\u0eb0\u0ec1\u0e81\u0e99\u0e82\u0eb2\u0e8d\u0ec4\u0e94\u0ec9\u0e17\u0eb1\u0e99\u0e97\u0eb5"}
              </p>
              <div className="flex w-full gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 sm:flex-none"
                  onClick={() => onOpenChange(false)}
                >
                  {"\u0e8d\u0ebb\u0e81\u0ec0\u0ea5\u0eb5\u0e81"}
                </Button>
                <Button
                  type="submit"
                  className="min-w-36 flex-1 gap-2 sm:flex-none"
                  disabled={provision.isPending}
                >
                  {provision.isPending ? <Loader className="size-4" /> : null}
                  {linkMode
                    ? "ຜູກກັບແລະເປີດຂາຍ"
                    : "\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81 \u0ec1\u0ea5\u0eb0 \u0ec0\u0e9b\u0eb5\u0e94\u0e82\u0eb2\u0e8d"}
                </Button>
              </div>
            </DialogFooter>
          </FormRoot>
        )}
      </DialogContent>
    </Dialog>
  );
}
